/**
 * PnlBridge.js — how a deal's P&L is built
 * ========================================
 * Brian, 5 October 2026, on Hilbert Group's convertible note reading -107k in
 * the list: "How are you calculating -$107k PnL? It would be nice to see how
 * you got to the on the deal report itself".
 *
 * The list prints one figure; this card takes it apart: cash back, less what
 * was paid out of it that is not capital (equity-line draws, market buys,
 * costs — D-285), plus what is still held, less the capital invested, each
 * into the ledger movements and balances it was added from. Every line is the server's (`stats.bridge`,
 * reports/services/deal_stats.py) and nothing is added up here, so the lines
 * and the list cannot disagree.
 *
 * On a deal held in krona and read in dollars it also says the thing the
 * figure cannot: cash back was converted on the day it came in and stays put,
 * while what is still held is converted at the latest fixing and moves with
 * the krona. On Hilbert a 1% move in the krona is worth more than four-tenths
 * of the P&L it reads, which is the difference between a loss and a currency.
 *
 * The figures carry cents: the point of the card is that somebody can add the
 * lines up and land on the total, and whole units rounded line by line do not
 * always add.
 */
import React, { useState } from "react";
import { Card } from "../../components/ui";
import { InfoTip } from "../../components/PageTools";
import theme from "../../components/theme";
import { day, dealMoney, exactMoney } from "./figures";
import { readerText } from "./text";
import { smallCaps } from "./FundingRounds";

export const bridgeMoney = exactMoney;

// A line in the currencies it is written in — "SEK 46,753,940.65" — when that
// is not already the figure beside it. Null on a line that converted nothing.
export function bridgeNativeText(line) {
  if (!line?.converted) return null;
  const parts = (line.native || []).filter((n) => Number(n.amount) !== 0);
  if (!parts.length) return null;
  return parts.map((n) => bridgeMoney(n.amount, n.currency)).join(" · ");
}

export const movementsText = (count) =>
  count ? `${count} movement${count === 1 ? "" : "s"}` : "";

// "SEK at 10.0579 per USD, the Riksbank fixing of 2 Oct 2026"
export function fixingText(fixing) {
  const rate = Number(fixing.per_usd).toFixed(4);
  return `${fixing.currency} at ${rate} per USD, the Riksbank fixing of ${day(fixing.fixed_on)}`;
}

// "Each 1% the SEK moves against the dollar moves what is still held — and
// so this P&L — by about $46,514."
export function sensitivityText(row) {
  return (
    `Each 1% the ${row.currency} moves against the dollar moves what is still held, ` +
    `and so this P&L, by about ${dealMoney(row.one_percent, "USD")}.`
  );
}

// "($2,862,755.83 + $4,651,421.74) ÷ $7,621,190.52 = 0.99x" — the MOIC the
// list prints, with the two figures it divides, which are the ones above.
export function moicParts(stats) {
  const bridge = stats.bridge;
  const c = bridge.currency;
  const back = bridge.cash_returned_net ?? bridge.cash_returned.total;
  return [
    `(${bridgeMoney(back, c)}`,
    `+ ${bridgeMoney(bridge.still_held.total, c)})`,
    `÷ ${bridgeMoney(bridge.invested.total, c)}`,
    `= ${Number(stats.moic).toFixed(2)}x`,
  ];
}

export const moicText = (stats) => moicParts(stats).join(" ");

const toneOf = (value) =>
  Number(value) > 0 ? theme.success : Number(value) < 0 ? theme.danger : theme.textSecondary;

function BridgeHead({ sign, label, total, currency, note }) {
  return (
    <div className="dr-bridge-row dr-bridge-head" style={{ borderTop: `1px solid ${theme.borderLight}` }}>
      <span className="dr-bridge-sign" style={{ color: theme.textMuted }}>{sign}</span>
      <div style={{ ...smallCaps, color: theme.text }}>
        {label}
        {note && (
          <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400, color: theme.textMuted }}>
            {" "}— {note}
          </span>
        )}
      </div>
      <span className="dr-bridge-amount" style={{ fontWeight: 600, color: theme.text }}>
        {bridgeMoney(total, currency)}
      </span>
    </div>
  );
}

// The movements one cash line was added from: the curve's own events of that
// kind — and, on a line the server split by instrument, of that instrument —
// one way. The server summed exactly these, so the list cannot disagree with
// the line, and nothing here adds them up again.
//
// Brian, 5 October 2026, on DevvStream's crypto convertible note: "$2.2mm in
// notes repaid? How is that possible?" The line said 21 movements and gave no
// way to see them; the answer was a prepayment, fourteen equity-line
// redemptions and six repayments of a second note, each one on the ledger.
//
// A short-sale line holds events of two kinds and both directions, and only
// part of each: `flow_types` names the kinds and `movement_field` the part.
// Those rows keep their sign, so a buy-back reads as money out under the sales
// it closed and the list still adds back to the line.
export function lineMovements(deal, line, inflow) {
  const flows = line.flow_types || [line.flow_type || line.key];
  const field = line.movement_field;
  const dealName = deal?.deal?.name;
  const out = [];
  (deal.series || []).forEach((point) => {
    (point.events || []).forEach((e) => {
      const amount = Number(e.amount);
      if (!flows.includes(e.flow_type)) return;
      if (!field && (amount > 0) !== inflow) return;
      if (line.split && (e.instrument ?? null) !== (line.instrument ?? null)) return;
      // An outflow line holds the capital part of each movement, or the part
      // paid out of what came back and why (D-285). An ACE deal's purchase can
      // be both: capital, less what the same day's sale paid for.
      let part = field ? Number(e[field] || 0) : amount;
      if (!field && (line.capital === true || line.capital === false)) {
        const capital = capitalPart(e);
        part = line.capital ? capital : amount - capital;
        if (!part) return;
        if (!line.capital && whyNotCapital(e) !== line.not_capital_because) return;
      }
      if (field && !part) return;
      const usd =
        e.amount_usd === undefined || e.amount_usd === null
          ? null
          : Number(e.amount_usd) * (amount ? part / amount : 0);
      const sign = field ? (line.side === "invested" || line.side === "paid_out" ? -1 : 1) : null;
      out.push({
        date: point.date,
        description: readerText(e.description, { dealName }),
        amount: sign ? part * sign : Math.abs(part),
        currency: e.currency || "USD",
        usd: usd === null ? null : sign ? usd * sign : Math.abs(usd),
      });
    });
  });
  return out;
}

// Why an outflow on the curve is not capital, or null when it is — the
// server's mark, read the way the server reads a series built without it.
export function whyNotCapital(event) {
  if (event.not_capital_because !== undefined) return event.not_capital_because;
  return event.flow_type === "other_out" ? "cost" : null;
}

// The part of an outflow that was capital, signed like the movement.
export function capitalPart(event) {
  if (event.capital_part !== undefined && event.capital_part !== null) return Number(event.capital_part);
  return whyNotCapital(event) === null ? Number(event.amount) : 0;
}

function Movements({ rows, currency, converted }) {
  return (
    <div className="dr-bridge-wide dr-bridge-moves" style={{ borderLeft: `2px solid ${theme.borderLight}` }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <tbody>
          {rows.map((m, i) => (
            <tr key={i} style={{ borderTop: i ? `1px solid ${theme.borderLight}` : "none" }}>
              <td style={{ padding: "4px 10px 4px 0", color: theme.textMuted, whiteSpace: "nowrap", verticalAlign: "top" }}>
                {day(m.date)}
              </td>
              <td style={{ padding: "4px 10px 4px 0", color: theme.textSecondary, verticalAlign: "top" }}>
                {m.description || "—"}
                {m.currency !== currency && (
                  <div className="dr-round-num" style={{ fontSize: 11, color: theme.textMuted }}>
                    {bridgeMoney(m.amount, m.currency)}
                  </div>
                )}
              </td>
              <td className="dr-round-num" style={{ padding: "4px 0", textAlign: "right", verticalAlign: "top", color: theme.textSecondary }}>
                {bridgeMoney(converted ? m.usd : m.amount, currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BridgeLine({ line, currency, movements, converted }) {
  const [open, setOpen] = useState(false);
  const native = bridgeNativeText(line);
  const canOpen = Boolean(movements && movements.length);
  const heading = (
    <>
      <span style={{ color: theme.text }}>{line.label}</span>
      {line.count ? <span style={{ color: theme.textMuted }}> · {movementsText(line.count)}</span> : null}
    </>
  );
  return (
    <>
      <div className="dr-bridge-row" title={line.reads_as || undefined}>
        <span />
        <div style={{ minWidth: 0 }}>
          {canOpen ? (
            <button
              type="button"
              className="dr-bridge-toggle"
              aria-expanded={open}
              onClick={() => setOpen((o) => !o)}
              style={{ background: "none", border: 0, padding: 0, font: "inherit", cursor: "pointer", textAlign: "left" }}
            >
              <span aria-hidden="true" style={{ color: theme.textMuted, display: "inline-block", width: 12 }}>
                {open ? "▾" : "▸"}
              </span>
              {heading}
            </button>
          ) : (
            heading
          )}
          <div style={{ fontSize: 11, color: theme.textMuted, paddingLeft: canOpen ? 12 : 0 }}>
            {[line.account, readerText(line.reads_as)].filter(Boolean).join(" · ")}
          </div>
        </div>
        {native && (
          <div className="dr-bridge-native" style={{ color: theme.textSecondary }}>
            <span className="dr-round-num">{native}</span>
          </div>
        )}
        <span className="dr-bridge-amount" style={{ color: theme.textSecondary }}>
          {bridgeMoney(line.amount, currency)}
        </span>
      </div>
      {open && (
        <div className="dr-bridge-row">
          <span />
          <Movements rows={movements} currency={currency} converted={converted} />
        </div>
      )}
    </>
  );
}

// The card names its deal. Brian, 5 October 2026, sent a screenshot of this
// card as Hilbert Group's when it was DevvStream's: nothing on the card said
// which one it was.
export const bridgeTitle = (deal) => `How the P&L is built — ${deal.deal.name}`;

export function PnlBridge({ deal }) {
  const stats = deal.stats;
  if (!stats) return null;
  if (stats.measured === false) {
    return (
      <Card title={bridgeTitle(deal)}>
        <div style={{ fontSize: 13, color: theme.textSecondary }}>
          No P&amp;L for this deal: {stats.withheld_because || "its figures could not be put in one currency"}.
        </div>
      </Card>
    );
  }
  // A measured block with no breakdown is a payload from before the
  // breakdown existed; there is nothing to take apart, and saying "no P&L"
  // would be false.
  if (!stats.bridge) return null;
  const bridge = stats.bridge;
  const c = bridge.currency;
  const held = bridge.still_held;
  // A payload from before D-285 has no paid-out section: everything was in
  // invested, and the card reads as it did.
  const paidOut = bridge.paid_out || { total: 0, lines: [] };
  const provisional = deal.summary && !deal.summary.basis_complete;
  return (
    <Card
      title={bridgeTitle(deal)}
      subtitle={`Cash back, net + what is still held − capital invested, from the general ledger · ${c}${
        bridge.converted ? `, converted from ${(deal.currency || []).join(" + ")}` : ""
      }`}
      action={
        <span className="dr-round-num" style={{ fontSize: 15, fontWeight: 600, color: toneOf(bridge.net_profit) }}>
          {bridgeMoney(bridge.net_profit, c)}
        </span>
      }
    >
      <div className="dr-bridge-frame">
        <div className="dr-bridge" style={{ fontSize: 13 }}>
          <BridgeHead
            sign=""
            label="Cash back"
            total={bridge.cash_returned.total}
            currency={c}
            note={bridge.converted ? "converted at the fixing of the day each came in" : null}
          />
          {bridge.cash_returned.lines.map((line) => (
            <BridgeLine
              key={line.key}
              line={line}
              currency={c}
              converted={bridge.converted}
              movements={lineMovements(deal, line, true)}
            />
          ))}
          {!bridge.cash_returned.lines.length && (
            <div className="dr-bridge-row"><span /><span className="dr-bridge-wide" style={{ color: theme.textMuted }}>Nothing has come back.</span></div>
          )}

          {paidOut.lines.length > 0 && (
            <>
              <BridgeHead sign="−" label="Paid out of it, not capital" total={paidOut.total} currency={c} />
              {paidOut.lines.map((line) => (
                <BridgeLine
                  key={line.key}
                  line={line}
                  currency={c}
                  converted={bridge.converted}
                  movements={lineMovements(deal, line, false)}
                />
              ))}
              <div className="dr-bridge-row dr-bridge-head" style={{ borderTop: `1px solid ${theme.borderLight}` }}>
                <span className="dr-bridge-sign" style={{ color: theme.textMuted }}>=</span>
                <div style={{ ...smallCaps, color: theme.text }}>Cash back, net</div>
                <span className="dr-bridge-amount" style={{ fontWeight: 600, color: theme.text }}>
                  {bridgeMoney(bridge.cash_returned_net, c)}
                </span>
              </div>
            </>
          )}

          <BridgeHead
            sign="+"
            label={held.lines.some((l) => l.key === "mark") ? "Still held, marked" : "Still held, at cost"}
            total={held.total}
            currency={c}
            note={held.fixings.length ? "converted at the latest fixing" : null}
          />
          {held.lines.map((line) => (
            <BridgeLine
              key={line.key}
              line={line}
              currency={c}
              converted={bridge.converted}
              movements={line.flow_types ? lineMovements(deal, line, true) : undefined}
            />
          ))}
          {!held.lines.length && (
            <div className="dr-bridge-row"><span /><span className="dr-bridge-wide" style={{ color: theme.textMuted }}>Nothing is still held.</span></div>
          )}
          {held.clamped && (
            <div className="dr-bridge-row">
              <span />
              <span style={{ color: theme.danger }}>Counted as nil — see the note below</span>
              <span className="dr-bridge-amount" style={{ color: theme.textSecondary }}>{bridgeMoney(0, c)}</span>
            </div>
          )}
          {held.fixings.map((f) => (
            <div key={f.currency} className="dr-bridge-row">
              <span />
              <span className="dr-bridge-wide" style={{ fontSize: 11, color: theme.textMuted }}>{fixingText(f)}</span>
            </div>
          ))}

          <BridgeHead sign="−" label="Invested, capital" total={bridge.invested.total} currency={c} />
          {bridge.invested.lines.map((line) => (
            <BridgeLine
              key={line.key}
              line={line}
              currency={c}
              converted={bridge.converted}
              movements={lineMovements(deal, line, false)}
            />
          ))}
          {!bridge.invested.lines.length && (
            <div className="dr-bridge-row"><span /><span className="dr-bridge-wide" style={{ color: theme.textMuted }}>No capital went in.</span></div>
          )}

          <div className="dr-bridge-row dr-bridge-head" style={{ borderTop: `2px solid ${theme.border}`, marginTop: 4 }}>
            <span className="dr-bridge-sign" style={{ color: theme.textMuted }}>=</span>
            <div style={{ ...smallCaps, color: theme.text }}>P&amp;L</div>
            <span className="dr-bridge-amount" style={{ fontWeight: 700, color: toneOf(bridge.net_profit) }}>
              {bridgeMoney(bridge.net_profit, c)}
            </span>
          </div>
          {stats.moic !== null && stats.moic !== undefined && (
            <div className="dr-bridge-row">
              <span />
              <div className="dr-bridge-wide" style={{ color: theme.textSecondary }}>
                MOIC is (cash back, net + still held) ÷ capital invested:{" "}
                {/* Wraps between its terms, never inside a figure. */}
                {moicParts(stats).map((part, i) => (
                  <React.Fragment key={i}>
                    {i ? " " : ""}
                    <span className="dr-round-num">{part}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
          {(stats.moic === null || stats.moic === undefined) && stats.moic_reads_as && (
            <div className="dr-bridge-row">
              <span />
              <div className="dr-bridge-wide" style={{ color: theme.textSecondary }}>
                No MOIC: {stats.moic_reads_as}.
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ fontSize: 12, color: theme.textMuted, marginTop: theme.space.md, display: "grid", gap: 4 }}>
        {provisional && (
          <div style={{ color: theme.danger }}>
            Provisional — some disposals have no cost loaded, so P&amp;L and MOIC overstate.
          </div>
        )}
        {bridge.fx_sensitivity.length > 0 && (
          <div>
            Cash back and what was invested are fixed at the rate of the day each moved; what is
            still held is converted at the latest fixing, so it moves with the currency.{" "}
            {bridge.fx_sensitivity.map(sensitivityText).join(" ")}
          </div>
        )}
        {held.notes.map((n) => (
          <div key={n}>{readerText(n)}</div>
        ))}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          Open a cash line for the movements it was added from.
          <InfoTip align="left" wide>
            A note or a preferred is carried at cost, never at face, so a discount it was bought at
            counts when it converts or is repaid, not before.
          </InfoTip>
        </div>
      </div>
    </Card>
  );
}
